import React, { useCallback, useMemo, useState } from 'react';
import { useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import {
  PiPlusLight,
  PiTrashLight,
  PiPencilSimpleLight,
  PiCaretDownLight,
  PiCheckLight,
  PiProhibitLight,
  PiInfoLight,
  PiArrowRightLight,
  PiArrowBendDownRightLight,
  PiTagLight,
  PiNoteLight,
} from 'react-icons/pi';
import { hideModal, showModal } from '../../store/actions/modalActions';
import { showSidePanel } from '../../store/actions/sidePanelActions';
import {
  deleteClauseRule,
} from '../../store/actions/Review/ReviewActions';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, clauseRulesTourSteps } from '../shared/FeatureTour';
import { ClauseRule } from '../../types/review';

/** Convert snake_case to Title Case: 'service_levels' → 'Service Levels' */
const formatClauseType = (text: string): string =>
  text
    .replace(/_/g, ' ')
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase());

interface ReviewClauseRulesProps {
  reviewUuid: string;
  clauseRules: ClauseRule[];
  readOnly?: boolean;
}

const ReviewClauseRules: React.FC<ReviewClauseRulesProps> = ({ reviewUuid, clauseRules, readOnly }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [riskFilter, setRiskFilter] = useState('all');

  // Tour
  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'clause-rules',
    steps: clauseRulesTourSteps,
    readyToShow: true,
    autoStartDelay: 600,
  });

  // ─── Filter + default-sort logic (by risk weight descending) ────────
  const filteredRules = useMemo(() => {
    let rules = [...clauseRules].sort((a, b) => (b.risk_weight || 0) - (a.risk_weight || 0));

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      rules = rules.filter((r) =>
        (r.display_name || '').toLowerCase().includes(q) ||
        r.clause_type.toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q) ||
        (r.tags || []).some((tag) => tag.toLowerCase().includes(q))
      );
    }

    if (riskFilter !== 'all') {
      rules = rules.filter((r) => {
        const rw = r.risk_weight || 1;
        if (riskFilter === 'high') return rw >= 8;
        if (riskFilter === 'medium') return rw >= 4 && rw <= 7;
        if (riskFilter === 'low') return rw <= 3;
        return true;
      });
    }

    return rules;
  }, [clauseRules, searchQuery, riskFilter]);

  // ─── Side panel handlers ─────────────────────────────────────────────

  const handleAddRule = useCallback(() => {
    dispatch(showSidePanel({
      type: 'editClauseRule',
      showSidePanel: true,
      width: '50%',
      message: t('New Clause Rule'),
      reviewUuid,
      actions: [
        { label: t('Save Rule'), variant: 'primary' as const, onClick: (ref: any) => ref?.save?.() },
      ],
    }));
  }, [dispatch, reviewUuid, t]);

  const handleEdit = useCallback((rule: ClauseRule) => {
    dispatch(showSidePanel({
      type: 'editClauseRule',
      showSidePanel: true,
      width: '50%',
      message: t('Edit Clause Rule'),
      reviewUuid,
      rule,
      actions: [
        { label: t('Update Rule'), variant: 'primary' as const, onClick: (ref: any) => ref?.save?.() },
      ],
    }));
  }, [dispatch, reviewUuid, t]);

  const handleDelete = useCallback((ruleUuid: string) => {
    dispatch(
      showModal({
        type: 'confirmation',
        message: t('Delete Clause Rule'),
        subMsg: t('This will permanently remove this clause rule from the playbook. This action cannot be undone.'),
        icon: <PiTrashLight className='w-6 h-6 text-red-600' />,
        isDest: true,
        showModal: true,
        onConfirm: async () => {
          await dispatch(deleteClauseRule(reviewUuid, ruleUuid) as any);
          dispatch(hideModal());
        },
        onCancel: () => dispatch(hideModal()),
      }),
    );
  }, [dispatch, reviewUuid, t]);

  const toggleExpand = useCallback((ruleUuid: string) => {
    setExpandedRuleId((prev) => prev === ruleUuid ? null : ruleUuid);
  }, []);

  return (
    <div className="pb-10 space-y-0">
      {/* Filter bar with search, risk filter, and add button */}
      <div data-tour-id="clause-rules-header" className="pt-4 px-2">
        <FilterBarWithTabs
          onSearchChange={setSearchQuery}
          filters={[
            {
              id: 'risk',
              name: t('Risk Level'),
              options: [
                { value: 'high', label: t('High (8-10)') },
                { value: 'medium', label: t('Medium (4-7)') },
                { value: 'low', label: t('Low (1-3)') },
              ],
            },
          ]}
          selectedFilters={{ risk: riskFilter !== 'all' ? [riskFilter] : [] }}
          onFilterChange={(filterName, values) => setRiskFilter(values.length > 0 ? values[0] : 'all')}
          actions={!readOnly ? [
            {
              label: t('Add Rule'),
              onClick: handleAddRule,
              icon: <PiPlusLight className="w-4 h-4" />,
            },
          ] : []}
          noParentPadding
          noBorder
        />
      </div>

      <div className="px-4 pt-4 space-y-2">

      {/* Inline form — kept commented out for reference after side panel migration */}
      {/* {showForm && !readOnly && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 space-y-3">
          ... inline form was here ...
        </div>
      )} */}

      {/* Rules list */}
      {filteredRules.length === 0 && clauseRules.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {readOnly
              ? t('No clause rules defined for this playbook.')
              : t('No clause rules yet. Add rules to define what clauses the AI should check.')}
          </p>
          {!readOnly && (
            <Button
              variant="neutral"
              size="small"
              className="mt-3"
              onClick={handleAddRule}
            >
              <PiPlusLight className="w-4 h-4 mr-1" />
              {t('Add First Rule')}
            </Button>
          )}
        </div>
      ) : filteredRules.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('No rules match your search.')}
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-tour-id="clause-rules-list">
          {filteredRules.map((rule) => {
            const rw = rule.risk_weight || 1;
            const riskBorder = rw >= 9 ? 'border-l-red-500' : rw >= 7 ? 'border-l-orange-500' : rw >= 4 ? 'border-l-amber-400' : 'border-l-emerald-500';
            const riskBadgeColor = (rw >= 9 ? 'red' : rw >= 7 ? 'orange' : rw >= 4 ? 'yellow' : 'green') as BadgeColor;
            const riskLabel = rw >= 8 ? t('High risk') : rw >= 4 ? t('Medium risk') : t('Low risk');
            const isExpanded = expandedRuleId === rule.uuid;

            return (
              <div
                key={rule.uuid}
                className={clsx(
                  'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 border-l-4 hover:shadow-sm transition-all duration-200',
                  riskBorder,
                )}
              >
                {/* Header row — always visible */}
                <button
                  type="button"
                  onClick={() => toggleExpand(rule.uuid)}
                  className="w-full text-left p-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                        {rule.display_name || formatClauseType(rule.clause_type)}
                      </span>
                      <Badge color="blue">{formatClauseType(rule.clause_type)}</Badge>
                      <Badge color={riskBadgeColor} tooltip={t('Risk weight (1-10): {{label}}', { label: riskLabel })}>
                        {t('Risk')} {rw}/10
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {!readOnly && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEdit(rule); }}
                            className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                            title={t('Edit')}
                          >
                            <PiPencilSimpleLight className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(rule.uuid); }}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                            title={t('Delete')}
                          >
                            <PiTrashLight className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                      <PiCaretDownLight
                        className={clsx(
                          'w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ml-1',
                          isExpanded && 'rotate-180',
                        )}
                      />
                    </div>
                  </div>
                  {rule.description && !isExpanded && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-1 leading-relaxed max-w-3xl">
                      {rule.description}
                    </p>
                  )}
                </button>

                {/* Expanded content — narrative flow with PiLight icons */}
                {isExpanded && (
                  <div className="border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30 px-4 py-4 space-y-5">
                    {/* What is this rule checking? */}
                    {rule.description && (
                      <div className="flex items-start gap-3">
                        <PiInfoLight className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0 max-w-2xl">
                          <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">{t('What this rule checks')}</p>
                          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 pl-3 pr-3 py-2">
                            <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">{rule.description}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Our preferred position */}
                    {rule.starting_position && (
                      <div className="flex items-start gap-3">
                        <PiArrowRightLight className="w-4 h-4 text-primary-500 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0 max-w-2xl">
                          <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">{t("Our preferred position")}</p>
                          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 pl-3 pr-3 py-2">
                            <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">{rule.starting_position}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Acceptable variations — light green card with PiCheckLight */}
                    {rule.acceptable_variations?.length > 0 && (
                      <div className="flex items-start gap-3">
                        <PiCheckLight className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0 max-w-2xl">
                          <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">{t("We accept")}</p>
                          <div className="bg-emerald-50 dark:bg-emerald-900/20 border-l-2 border-emerald-400 dark:border-emerald-600 pl-3 pr-3 py-2 space-y-1">
                            {rule.acceptable_variations.map((v, i) => (
                              <p key={i} className="text-sm text-emerald-900 dark:text-emerald-100 leading-relaxed">{v}</p>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Red lines — light red card with PiProhibitLight */}
                    {rule.red_lines?.length > 0 && (
                      <div className="flex items-start gap-3">
                        <PiProhibitLight className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0 max-w-2xl">
                          <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">{t("We won't accept")}</p>
                          <div className="bg-red-50 dark:bg-red-900/20 border-l-2 border-red-400 dark:border-red-600 pl-3 pr-3 py-2 space-y-1">
                            {rule.red_lines.map((rl, i) => (
                              <p key={i} className="text-sm text-red-900 dark:text-red-100 leading-relaxed">{rl}</p>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Fallback position */}
                    {rule.fallback_position && (
                      <div className="flex items-start gap-3">
                        <PiArrowBendDownRightLight className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0 max-w-2xl">
                          <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">{t("If we must compromise")}</p>
                          <div className="bg-amber-50 dark:bg-amber-900/20 border-l-2 border-amber-400 dark:border-amber-600 pl-3 pr-3 py-2">
                            <p className="text-sm text-amber-900 dark:text-amber-100 leading-relaxed">{rule.fallback_position}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Tags + Notes */}
                    {(rule.tags?.length > 0 || rule.notes) && (
                      <div className="pt-2 border-t border-gray-200 dark:border-gray-700 space-y-3">
                        {rule.tags?.length > 0 && (
                          <div className="flex items-start gap-3">
                            <PiTagLight className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0 max-w-2xl">
                              <div className="flex flex-wrap gap-1">
                                {rule.tags.map((tag, i) => (
                                  <Badge key={i} color="purple">{tag}</Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                        {rule.notes && (
                          <div className="flex items-start gap-3">
                            <PiNoteLight className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0 max-w-2xl">
                              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed italic">{rule.notes}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      </div>{/* end content area */}

      <FeatureTour
        steps={clauseRulesTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </div>
  );
};

export default ReviewClauseRules;
